<template>
	<div
		class="thread-created-outer"
		:class="{ 'has-connector': showCreatorLine }"
	>
		<div
			v-if="showCreatorLine && creatorName"
			class="thread-source-creator-line"
		>
			<ReelIcon class="thread-source-reel-icon" type="solid" />
			<i18n-t keypath="model_systemevent_threadcreated">
				<span class="thread-created-who">{{ creatorName }}</span>
			</i18n-t>
		</div>
		<SourceMessagePreview
			v-if="
				!showCreatorLine &&
				!hideSourcePreview &&
				sourceHasMedia &&
				sourceMessageId
			"
			class="thread-source-media-preview"
			:messageId="sourceMessageId"
		/>
		<div
			v-else-if="!showCreatorLine && !hideSourcePreview && sourceMessageText"
			class="thread-source-text-preview"
		>
			{{ sourceMessageText }}
		</div>
		<div class="thread-created-wrapper">
			<div class="thread-elbow" />
			<div
				class="thread-created-card"
				:class="{ 'thread-deleted': deleted }"
				@click="!deleted && openThread()"
			>
				<div class="thread-card-content">
					<template v-if="deleted">
						<div class="thread-card-preview">
							<span class="preview-text thread-deleted-text">{{
								$t('chat_thread_preview_deleted')
							}}</span>
						</div>
					</template>
					<template v-else>
						<div class="thread-card-header">
							<span class="thread-title">{{ title }}</span>
							<span class="all-messages-link">
								{{ $t('chat_thread_preview_all_messages') }}
								<ChevronIcon class="chevron" type="solid" right />
							</span>
						</div>
						<div v-if="latestMessage" class="thread-card-preview">
							<ProfilePic :user="latestUser" size="16px" />
							<span class="preview-text">{{ previewText }}</span>
							<span class="preview-time">
								<RelativeTime :date="getLocalDate(latestMessage.created_at)" />
							</span>
						</div>
						<div v-else-if="loading" class="thread-card-preview loading">
							<div class="skeleton skeleton-avatar" />
							<div class="skeleton skeleton-text" />
							<div class="skeleton skeleton-time" />
						</div>
						<div v-else class="thread-card-preview">
							<span class="preview-text no-messages">{{
								$t('chat_thread_preview_no_messages')
							}}</span>
						</div>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { ProfilePic, RelativeTime } from '../common'
import { ChevronIcon, ReelIcon } from '@/icons'
import { getChat, getMessage, getUser, getMessagesForBubble } from '@/store'
import { parseMessageParts, partType } from './utils'
import { fetchChat, fetchHistory } from '@/api/chat'
import { getLocalDate } from '@/helpers/utils'
import { replaceMentions } from '@/components/Editor/utils'
import {
	subscribeToThreadPreview,
	resubscribeThreadPreview,
} from '@/helpers/threadPreviewPusher'
import SourceMessagePreview, { defaultMediaText } from './SourceMessagePreview'

export default {
	name: 'ThreadCreatedCard',

	components: {
		ProfilePic,
		RelativeTime,
		ChevronIcon,
		ReelIcon,
		SourceMessagePreview,
	},

	props: {
		threadId: {
			type: Number,
			required: true,
		},
		showCreatorLine: {
			type: Boolean,
			default: false,
		},
		hideSourcePreview: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			loading: false,
			deleted: false,
			loaded: false,
			cachedTitle: '',
		}
	},

	computed: {
		thread() {
			return getChat(this.threadId)
		},

		title() {
			return (
				this.thread?.title || this.thread?.displayname || this.cachedTitle || ''
			)
		},

		latestMessage() {
			return this.getLatestUserMessage()
		},

		latestUser() {
			if (!this.latestMessage?.user_id) return null
			return getUser(this.latestMessage.user_id)
		},

		threadCreator() {
			const userId = this.thread?.user_id
			if (!userId) return null
			return getUser(userId)
		},

		creatorName() {
			return this.threadCreator?.fullname || this.threadCreator?.full_name || ''
		},

		sourceMessageId() {
			return this.thread?.source_message_id || null
		},

		sourceMessage() {
			if (!this.sourceMessageId) return null
			return getMessage(this.sourceMessageId)
		},

		sourceHasMedia() {
			return (
				this.sourceMessage?.hasMedia ||
				this.sourceMessage?.resource ||
				this.sourceMessage?.poll_id
			)
		},

		sourceMessageText() {
			if (!this.sourceMessageId) return null
			if (this.sourceHasMedia) return null
			const text = this.sourceMessage?.text || this.thread?.title || null
			return text ? replaceMentions(text) : null
		},

		previewText() {
			if (!this.latestMessage) return ''
			const text = parseMessageParts(this.latestMessage)
				.map(({ type, userId, user, text }) => {
					switch (type) {
						case partType.MENTION:
							if (user) return `@${user.fullname}`
							if (userId === 'everyone') return '@everyone'
							return `@User #${userId}`
						case partType.HYPERLINK:
						case partType.TEXT:
							return text
						default:
							return null
					}
				})
				.filter((part) => part)
				.join('')
			if (text) return text
			if (this.latestMessage.hasMedia) {
				if (this.latestMessage.mediaCount > 1) {
					return this.$t('chat_message_mediaaction_multiple', {
						Count: this.latestMessage.mediaCount,
					})
				}
				const key = defaultMediaText[this.latestMessage.media?.mediatype]
				if (key) return this.$t(key)
			}
			return ''
		},
	},

	watch: {
		thread: {
			handler(chat) {
				const title = chat?.title || chat?.displayname
				if (title) this.cachedTitle = title
				const exists = chat && Object.keys(chat).length
				if (this.deleted && exists) {
					// Recovery: thread appeared after being marked deleted
					// (race condition — Pusher event before REST availability)
					this.deleted = false
					this.loadThreadPreview()
				} else if (!this.deleted && this.loaded && !exists) {
					// Thread data was evicted from the store (e.g. diffChatsInCache
					// removed it because the user has no or a hidden membership).
					// Re-fetch just the chat object — messages and Pusher
					// subscription are still intact.
					this.refetchThread()
				}
			},
		},
	},

	mounted() {
		// Defer fetch + Pusher subscription until card is visible
		this._observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					this._observer.disconnect()
					this._observer = null
					this.loadThreadPreview()
				}
			},
			{ rootMargin: '200px' }
		)
		this._observer.observe(this.$el)

		this.$onEventBus('chat-removed', (chatId) => {
			if (chatId === this.threadId) {
				this.refetchThread()
			}
		})

		// When the thread panel closes, ChatPanel's unload() kills the
		// shared Pusher channel. Reconnect so new messages keep flowing
		// into the store. Uses setTimeout to run after Vue unmounts
		// ChatPanel (which triggers the channel kill).
		this.$onEventBus('close-thread', () => {
			if (!this._unsubscribePreview) return
			setTimeout(() => {
				if (this._destroyed) return
				resubscribeThreadPreview(this.threadId)
			}, 0)
		})
	},

	beforeUnmount() {
		this._destroyed = true
		if (this._observer) {
			this._observer.disconnect()
			this._observer = null
		}
		if (this._unsubscribePreview) {
			this._unsubscribePreview()
			this._unsubscribePreview = null
		}
	},

	methods: {
		getLocalDate,

		getLatestUserMessage() {
			const messages = getMessagesForBubble(this.threadId)
			// messages are sorted oldest-first, so reverse to find latest non-system
			for (let i = messages.length - 1; i >= 0; i--) {
				if (!messages[i].systemevent) return messages[i]
			}
			return null
		},

		async refetchThread() {
			try {
				await fetchChat(this.threadId)
			} catch (e) {
				if (this._destroyed) return
				if (e?.response?.status === 404) {
					this.deleted = true
				}
			}
		},

		async loadThreadPreview() {
			// Only show skeleton if no cached message
			if (!this.latestMessage) {
				this.loading = true
			}

			// Ensure thread chat is in the store (needed for Pusher channelcode)
			const chat = getChat(this.threadId)
			if (!chat || !Object.keys(chat).length) {
				try {
					await fetchChat(this.threadId)
				} catch (e) {
					if (this._destroyed) return
					if (e?.response?.status === 404) {
						this.deleted = true
						this.loading = false
						return
					}
				}
				if (this._destroyed) return
			}

			// Fetch thread message history into the store.
			// latestMessage is a computed that reads the store reactively,
			// so it updates automatically when messages are added.
			try {
				await fetchHistory({ bubble_id: this.threadId })
				if (this._destroyed) return
			} catch (e) {
				if (this._destroyed) return
				if (e?.response?.status === 404) {
					this.deleted = true
					return
				}
			} finally {
				this.loading = false
			}

			// Subscribe to Pusher to pump new messages into the store.
			// The computed latestMessage picks them up via store reactivity.
			if (!this._destroyed && !this.deleted) {
				this.loaded = true
				this._unsubscribePreview = subscribeToThreadPreview(this.threadId)
			}
		},

		openThread() {
			EventBus.$emit('open-thread', getChat(this.threadId))
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.thread-created-outer {
	display: flex;
	flex-direction: column;
	position: relative;

	&.has-connector {
		padding-top: 20px;
	}
}

.thread-source-creator-line {
	display: flex;
	align-items: center;
	gap: 3px;
	font-family: Avenir-Heavy;
	font-size: 12px;
	color: var(--neutral-600);
	margin-left: 14px;
	margin-bottom: 4px;
	margin-top: 3px;

	.thread-created-who {
		color: var(--neutral-800);
	}
}

.thread-source-reel-icon {
	font-size: 17px;
	color: var(--blue-300);
	transform: rotate(30deg);
	margin-right: 1px;
}

.thread-source-media-preview {
	margin-left: 14px;
	margin-right: 16px;
	margin-top: 3px;
	margin-bottom: 8px;
}

.thread-source-text-preview {
	font-family: Avenir-Medium;
	font-size: 14px;
	color: var(--neutral-600);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: fit-content;
	max-width: 389px;
	margin-left: 14px;
	margin-top: 3px;
	margin-bottom: 8px;
	background: var(--neutral-100);
	border-radius: 14px;
	padding: 6px 12px;
}

.thread-created-wrapper {
	display: flex;
	margin-left: 23px;
	margin-right: 16px;
	margin-bottom: 8px;
}

.thread-elbow {
	width: 22px;
	height: 35px;
	flex-shrink: 0;
	border-left: 2px solid var(--blue-300);
	border-bottom: 2px solid var(--blue-300);
	border-bottom-left-radius: 10px;
	align-self: flex-start;
	margin-right: 8px;
}

.thread-created-card {
	position: relative;
	display: flex;
	align-items: stretch;
	min-width: 0;
	width: 350px;
	height: 67px;
	box-sizing: border-box;
	cursor: pointer;
	border-radius: 8px;
	background: var(--neutral-100);
	padding: 12px;

	&.thread-deleted {
		cursor: default;
	}
}

.thread-deleted-text {
	font-style: italic;
	color: var(--neutral-500);
	padding-right: 2px;
}

.no-messages {
	padding-right: 2px;
}

.thread-card-content {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
}

.thread-card-header {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 6px;
}

.thread-title {
	font-family: Avenir-Heavy;
	font-size: 15px;
	color: var(--neutral-800);
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.all-messages-link {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	flex-shrink: 0;
	white-space: nowrap;
	font-family: Avenir-Medium;
	font-size: 14px;
	color: var(--blue-300);
	text-decoration: underline;
	cursor: pointer;

	.chevron {
		font-size: 10px;
		text-decoration: none;
	}
}

.thread-card-preview {
	display: flex;
	align-items: center;
	gap: 8px;

	&.loading {
		gap: 8px;
	}

	:deep(.profile-pic-avatar) {
		flex-shrink: 0;
	}
}

.preview-text {
	min-width: 0;
	font-size: 14px;
	line-height: 16px;
	font-family: Avenir-Roman;
	color: var(--neutral-800);

	&.no-messages,
	&.thread-deleted-text {
		font-style: italic;
		color: var(--neutral-500);
	}
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.preview-time {
	font-family: Avenir-Roman;
	flex-shrink: 0;
	font-size: 12px;
	color: var(--neutral-500);
}

.skeleton {
	border-radius: 4px;
	background: var(--neutral-200);
	animation: shimmer 1.5s ease-in-out infinite;

	&-avatar {
		width: 16px;
		height: 16px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	&-text {
		width: 60%;
		height: 14px;
		flex: 1;
	}

	&-time {
		width: 40px;
		height: 12px;
		flex-shrink: 0;
	}
}

@keyframes shimmer {
	0%,
	100% {
		opacity: 0.4;
	}
	50% {
		opacity: 0.8;
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.thread-created-card {
		background: var(--neutral-0);

		.skeleton {
			background: var(--neutral-100);
		}
	}

	.thread-source-text-preview {
		background: var(--neutral-0);
	}
}
</style>
